const BASE_DOMAIN = 'commit.you';

/** Cached hostname check for performance */
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

/** Check if running on localhost */
export const isLocalhost = hostname === 'localhost' || hostname.includes('127.0.0.1');

/**
 * Validates a commit ID format (8-character alphanumeric).
 */
export function isValidCommitId(id: string): boolean {
  return /^[a-zA-Z0-9]{8}$/.test(id);
}

/**
 * Get the URL for a user's profile.
 * In production, uses subdomain format (username.commit.you).
 * In development, uses path format (/username).
 */
export function getProfileUrl(username: string, branch?: string): string {
  // Local development - use path-based routing
  if (isLocalhost) {
    return branch ? `/${username}/${branch}` : `/${username}`;
  }

  // Production - use subdomain
  const baseUrl = `https://${username}.${BASE_DOMAIN}`;
  return branch ? `${baseUrl}/${branch}` : baseUrl;
}

/**
 * Check if we're on the main domain (not a subdomain).
 */
function isMainDomain(): boolean {
  return (
    isLocalhost ||
    hostname === BASE_DOMAIN ||
    hostname === `www.${BASE_DOMAIN}` ||
    // Not a subdomain pattern (no prefix before commit.you)
    !hostname.endsWith(`.${BASE_DOMAIN}`)
  );
}

/**
 * Get the URL for the main app/feed.
 * Returns relative path if already on main domain for SPA navigation.
 */
export function getAppUrl(): string {
  // Main domain - use relative path for SPA navigation
  if (isMainDomain()) {
    return '/';
  }

  // On subdomain - return full URL to main domain
  return `https://${BASE_DOMAIN}`;
}

/**
 * Get the URL for a commit detail page.
 * Returns relative path if on main domain for SPA navigation.
 * Use absolute=true for sharing/clipboard (always returns full URL).
 */
export function getCommitUrl(commitId: string, absolute = false): string {
  // For sharing - always return full URL
  if (absolute) {
    return `https://${BASE_DOMAIN}/c/${commitId}`;
  }

  // Main domain - use relative path for SPA navigation
  if (isMainDomain()) {
    return `/c/${commitId}`;
  }

  // On subdomain - return full URL to main domain
  return `https://${BASE_DOMAIN}/c/${commitId}`;
}

/**
 * Get the URL for a tag page.
 * Returns relative path if on main domain for SPA navigation.
 */
export function getTagUrl(tag: string): string {
  // Main domain - use relative path for SPA navigation
  if (isMainDomain()) {
    return `/tag/${tag}`;
  }

  // On subdomain - return full URL to main domain
  return `https://${BASE_DOMAIN}/tag/${tag}`;
}

/**
 * Get the current subdomain from the URL, if any.
 */
export function getCurrentSubdomain(): string | null {
  if (isLocalhost) {
    return null;
  }

  if (hostname.endsWith(`.${BASE_DOMAIN}`)) {
    return hostname.replace(`.${BASE_DOMAIN}`, '');
  }

  return null;
}

/**
 * Get the URL for navigating to a branch on a user's profile.
 * Returns just the path if already on that user's subdomain.
 */
export function getBranchUrl(username: string, branch: string | null): string {
  const currentSubdomain = getCurrentSubdomain();

  // If on the same user's subdomain, just return the path
  if (currentSubdomain && currentSubdomain.toLowerCase() === username.toLowerCase()) {
    return branch ? `/${branch}` : '/';
  }

  // Otherwise, return full URL
  if (isLocalhost) {
    return branch ? `/${username}/${branch}` : `/${username}`;
  }

  const baseUrl = `https://${username}.${BASE_DOMAIN}`;
  return branch ? `${baseUrl}/${branch}` : baseUrl;
}
